import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { videos, categories } from '@/mocks/videos';
import type { Video } from '@/mocks/videos';

const categorySlugMap: Record<string, string> = {
  'all': '全部',
  'dianshiju': '电视剧',
  'dianying': '电影',
  'zongyi': '综艺',
  'dongman': '动漫',
  'jilupian': '纪录片',
  'duanshipin': '短视频',
  'tiyu': '体育',
  'yinyue': '音乐',
  'youxi': '游戏',
  'zixun': '资讯',
};

const slugMap: Record<string, string> = {
  '全部': 'all',
  '电视剧': 'dianshiju',
  '电影': 'dianying',
  '综艺': 'zongyi',
  '动漫': 'dongman',
  '纪录片': 'jilupian',
  '短视频': 'duanshipin',
  '体育': 'tiyu',
  '音乐': 'yinyue',
  '游戏': 'youxi',
  '资讯': 'zixun',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState('全部');
  const [filteredVideos, setFilteredVideos] = useState<Video[]>(videos);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [slug]);

  useEffect(() => {
    const categoryFromSlug = categorySlugMap[slug || 'all'] || '全部';
    setActiveCategory(categoryFromSlug);

    if (categoryFromSlug === '全部') {
      setFilteredVideos(videos);
    } else if (categoryFromSlug === '资讯') {
      setFilteredVideos([]);
    } else {
      setFilteredVideos(videos.filter((v) => v.category === categoryFromSlug));
    }
  }, [slug]);

  const handleCategoryChange = (category: string) => {
    setActiveCategory(category);
    if (category === '全部') {
      setFilteredVideos(videos);
    } else {
      setFilteredVideos(videos.filter((v) => v.category === category));
    }
  };

  const pageTitle = activeCategory === '全部'
    ? '全部视频 - 黄瓜视频在线'
    : `${activeCategory} - 黄瓜视频在线`;

  useEffect(() => {
    document.title = pageTitle;
  }, [pageTitle]);

  const categorySchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': `https://www.huangguaTV.com/category/${slug}#webpage`,
        url: `https://www.huangguaTV.com/category/${slug}`,
        name: pageTitle,
        inLanguage: 'zh-CN',
        isPartOf: { '@id': 'https://www.huangguaTV.com/#website' },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            {
              '@type': 'ListItem',
              position: 1,
              name: '首页',
              item: 'https://www.huangguaTV.com/',
            },
            {
              '@type': 'ListItem',
              position: 2,
              name: activeCategory,
            },
          ],
        },
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(categorySchema) }}
      />

      <Navbar />

      <main className="pt-20 bg-background-50">
        <div className="w-full px-4 md:px-6 py-6">
          <div className="max-w-[1280px] mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: activeCategory },
              ]}
            />
          </div>
        </div>

        <section className="w-full px-4 md:px-6 pb-14 md:pb-20">
          <div className="max-w-[1280px] mx-auto">
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 mb-2">
              {activeCategory === '全部' ? '全部视频' : activeCategory}
            </h1>
            <p className="text-sm text-foreground-500 mb-6">
              {activeCategory === '全部'
                ? '浏览黄瓜视频在线的所有视频内容'
                : `浏览黄瓜视频在线精选${activeCategory}内容，共 ${filteredVideos.length} 个视频`}
            </p>

            <div className="flex flex-wrap items-center gap-2 mb-8">
              {categories.map((cat) => (
                <Link
                  key={cat}
                  to={`/category/${slugMap[cat] || 'all'}`}
                  onClick={(e) => {
                    if (slugMap[cat] === slug) return;
                    handleCategoryChange(cat);
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap cursor-pointer ${
                    activeCategory === cat
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat}
                </Link>
              ))}
            </div>

            {activeCategory === '资讯' ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-5 flex items-center justify-center rounded-2xl bg-background-100">
                  <i className="ri-article-line text-3xl text-foreground-400"></i>
                </div>
                <h3 className="font-heading text-xl text-foreground-900 mb-2">资讯栏目</h3>
                <p className="text-sm text-foreground-500 mb-6">
                  请访问首页查看最新资讯文章，或点击下方按钮跳转
                </p>
                <Link
                  to="/"
                  className="inline-flex px-6 py-2.5 rounded-full text-sm font-medium bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  回到首页
                </Link>
              </div>
            ) : filteredVideos.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 mx-auto mb-5 flex items-center justify-center rounded-2xl bg-background-100">
                  <i className="ri-video-line text-3xl text-foreground-400"></i>
                </div>
                <h3 className="font-heading text-xl text-foreground-900 mb-2">暂无内容</h3>
                <p className="text-sm text-foreground-500">该分类下暂无视频，请浏览其他分类</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {filteredVideos.map((video) => (
                  <Link
                    key={video.id}
                    to={`/category/${slug}`}
                    className="group bg-background-100 rounded-xl overflow-hidden cursor-pointer transition-all duration-300 hover:scale-[1.02]"
                  >
                    <div className="relative w-full aspect-video overflow-hidden">
                      <img
                        src={video.thumbnail}
                        alt={video.title}
                        title={`${video.title} - 黄瓜视频在线${video.category}频道`}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent"></div>
                      <div className="absolute top-3 left-3">
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-primary-500 text-white">
                          {video.quality}
                        </span>
                      </div>
                      <div className="absolute bottom-3 right-3">
                        <span className="text-xs text-white/90 bg-black/50 px-2 py-0.5 rounded">
                          {video.duration}
                        </span>
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="w-14 h-14 flex items-center justify-center rounded-full bg-primary-500/90 text-white">
                          <i className="ri-play-fill text-2xl ml-0.5"></i>
                        </div>
                      </div>
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-base text-foreground-900 mb-1.5 line-clamp-1">
                        {video.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-1 mb-3">
                        {video.description}
                      </p>
                      <div className="flex items-center gap-3 text-xs text-foreground-500">
                        <span className="flex items-center gap-1">
                          <i className="ri-eye-line w-3.5 h-3.5 flex items-center justify-center"></i>
                          {video.views}
                        </span>
                        <span className="flex items-center gap-1">
                          <i className="ri-star-fill text-accent-500 w-3.5 h-3.5 flex items-center justify-center"></i>
                          {video.rating}
                        </span>
                        <span className="ml-auto">
                          <span className="px-1.5 py-0.5 rounded text-xs bg-secondary-100 text-secondary-700">
                            {video.category}
                          </span>
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}